/**
 * Global error handling setup for production
 * Prevents uncaught errors and unhandled promise rejections from appearing in production console
 */

import { logger, productionSafeCriticalLogger } from '@/utils/logger'
import { isDevelopment } from '@/utils/environment'

export const setupGlobalErrorHandling = () => {
    // Handle uncaught JavaScript errors
    globalThis.addEventListener('error', (event) => {
        const { message, filename, lineno, colno, error } = event

        if (isDevelopment) {
            // In development, log for debugging but don't prevent default behavior
            logger.error('Uncaught error:', {
                message,
                filename,
                line: lineno,
                column: colno,
                stack: error?.stack,
            })
        } else {
            // In production, log critical errors but prevent console output
            productionSafeCriticalLogger.error('Production error:', {
                message: message || 'Unknown error',
                filename: filename || 'Unknown file',
                timestamp: new Date().toISOString(),
            })

            // Prevent the error from appearing in console
            event.preventDefault()
        }
    })

    // Handle unhandled promise rejections
    globalThis.addEventListener('unhandledrejection', (event) => {
        const reason = event.reason

        if (isDevelopment) {
            // In development, log for debugging
            logger.error('Unhandled promise rejection:', {
                reason:
                    reason instanceof Error ? reason.message : String(reason),
                stack: reason instanceof Error ? reason.stack : undefined,
            })
        } else {
            // In production, log critical info but prevent console output
            productionSafeCriticalLogger.error(
                'Production promise rejection:',
                {
                    reason:
                        reason instanceof Error
                            ? reason.message
                            : 'Promise rejected',
                    timestamp: new Date().toISOString(),
                },
            )

            // Prevent the unhandled rejection from appearing in console
            event.preventDefault()
        }
    })

    // Override console methods in production to suppress all console output
    if (!isDevelopment) {
        // Override all console methods to be no-ops in production
        // Using Object.defineProperty to avoid linter issues
        const noop = () => {}

        Object.defineProperty(console, 'log', { value: noop, writable: true })
        Object.defineProperty(console, 'warn', { value: noop, writable: true })
        Object.defineProperty(console, 'info', { value: noop, writable: true })
        Object.defineProperty(console, 'debug', { value: noop, writable: true })
        Object.defineProperty(console, 'trace', { value: noop, writable: true })
        Object.defineProperty(console, 'assert', {
            value: noop,
            writable: true,
        })
        Object.defineProperty(console, 'group', { value: noop, writable: true })
        Object.defineProperty(console, 'groupEnd', {
            value: noop,
            writable: true,
        })
        Object.defineProperty(console, 'table', { value: noop, writable: true })
        Object.defineProperty(console, 'time', { value: noop, writable: true })
        Object.defineProperty(console, 'timeEnd', {
            value: noop,
            writable: true,
        })
        Object.defineProperty(console, 'clear', { value: noop, writable: true })
        Object.defineProperty(console, 'count', { value: noop, writable: true })
        Object.defineProperty(console, 'countReset', {
            value: noop,
            writable: true,
        })
        Object.defineProperty(console, 'dir', { value: noop, writable: true })
        Object.defineProperty(console, 'dirxml', {
            value: noop,
            writable: true,
        })
        Object.defineProperty(console, 'profile', {
            value: noop,
            writable: true,
        })
        Object.defineProperty(console, 'profileEnd', {
            value: noop,
            writable: true,
        })
        Object.defineProperty(console, 'error', { value: noop, writable: true })
    }
}

export default setupGlobalErrorHandling
